/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import * as Yup from 'yup';
import { useCheckoutStore } from '../../../../Hooks/Zustand/useCheckoutStore';
import { useFormContentProvider } from '../../../../Providers/FormContentProvider';
import { isIbanValid, nextButtonActive } from '../../../../Signals/formStateSignal';
import { checkoutFormModel } from '../../FormModel/FormModel';

/**
 * Custom PaymentForm hook
 * @returns {{isLoading, studioData}}
 */
export function usePaymentForm() {
  const { getFormDataValue, updateFormDataValue } = useCheckoutStore();
  const {
    setFieldValue,
    values: { paymentChoice, adyenPaymentState },
  } = useFormikContext();
  const { setValidationSchema } = useFormContentProvider();
  const {
    formField: { accountOwner, iban, paymentTerms },
  } = checkoutFormModel;

  /**
     * Return the saved tab panel state from store
     * @returns {number}
     */
  const getInitialTabPanel = () => {
    if (getFormDataValue('paymentChoice')) {
      switch (getFormDataValue('paymentChoice')) {
        case 'DIRECT_DEBIT':
          return 0;
        case 'CREDIT_CARD':
          return 1;
        default:
          return 0;
      }
    } else {
      return 0;
    }
  };

  const [tab, setTab] = useState(getInitialTabPanel());

  /**
     * Update the formik validation schema dynamically
     */
  useEffect(() => {
    // Use the hard coded direct_debit validation schema
    if (paymentChoice === 'DIRECT_DEBIT') {
      nextButtonActive.value = true;
      setValidationSchema(
        Yup.object().shape({
          [accountOwner.name]: Yup.string().required(`${accountOwner.requiredErrorMsg}`),
          [iban.name]: Yup.string()
            .required(`${iban.requiredErrorMsg}`)
            .test('iban-test', iban.notValidErrorMsg, () => isIbanValid.value),
          [paymentTerms.name]: Yup.bool().required(`${paymentTerms.requiredErrorMsg}`),
        }),
      );
    }
    /**
         * Due to the reason the credit card checkout is an external form panel we need to reset the validation schema.
         * We disable the next button and activate it after the customer has successfully validated his credit card
         */
    if (paymentChoice === 'CREDIT_CARD') {
      setValidationSchema(Yup.object().shape({}));
      nextButtonActive.value = adyenPaymentState === 'PAYMENT_COMPLETE';
    }
  }, [
    accountOwner.name,
    accountOwner.requiredErrorMsg,
    adyenPaymentState,
    iban.name,
    iban.notValidErrorMsg,
    iban.requiredErrorMsg,
    paymentChoice,
    paymentTerms.name,
    paymentTerms.requiredErrorMsg,
    setValidationSchema,
  ]);

  /**
     * Handle Tab Change
     * @param newValue
     */
  const handleTabChange = (newValue) => {
    setTab(newValue);
    switch (newValue) {
      case 0:
        setFieldValue('paymentChoice', 'DIRECT_DEBIT');
        updateFormDataValue('paymentChoice', 'DIRECT_DEBIT');
        break;
      case 1:
        setFieldValue('paymentChoice', 'CREDIT_CARD');
        updateFormDataValue('paymentChoice', 'CREDIT_CARD');
        break;
      default:
        // eslint-disable-next-line no-console
        console.error('Payment choice is not allowed');
    }
  };

  return {
    tab,
    setTab,
    handleTabChange,
  };
}
